import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import {
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Copy,
  Mail,
  Loader2,
  CheckCircle2,
  UserCheck,
  Undo2,
  Send,
  Trash2,
} from 'lucide-react';

/**
 * Bloques del panel de admin. Cada uno responde una pregunta:
 * ¿Estoy vendiendo? ¿Lo están usando? ¿Qué tengo que hacer?
 * Los datos llegan ya calculados de /api/admin/panel (backend/panel_admin.py).
 */

export function fmtMoney(n) {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 })
    .format(n || 0);
}

export function fmtFecha(iso, conHora = false) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      ...(conHora ? { hour: '2-digit', minute: '2-digit' } : {}),
    });
  } catch {
    return iso;
  }
}

async function copiarMails(emails) {
  if (!emails.length) return;
  try {
    await navigator.clipboard.writeText(emails.join(', '));
    toast.success(`${emails.length} ${emails.length === 1 ? 'mail copiado' : 'mails copiados'}`);
  } catch {
    toast.error('No se pudo copiar (revisá los permisos del navegador)');
  }
}

function BotonesMails({ emails, testid }) {
  if (!emails.length) return null;
  return (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="outline"
        onClick={() => copiarMails(emails)}
        data-testid={testid}
        className="h-7 bg-transparent border-white/10 text-slate-200 hover:bg-white/5 hover:text-white text-xs"
      >
        <Copy className="h-3 w-3 mr-1.5" />
        Copiar {emails.length}
      </Button>
      <a href={`mailto:?bcc=${encodeURIComponent(emails.join(';'))}`}>
        <Button
          size="sm"
          variant="outline"
          className="h-7 bg-transparent border-white/10 text-slate-200 hover:bg-white/5 hover:text-white text-xs"
        >
          <Mail className="h-3 w-3 mr-1.5" />
          Mail
        </Button>
      </a>
    </div>
  );
}

/** Cambio contra el período anterior. Sin anterior no se inventa un porcentaje. */
function Variacion({ actual, anterior }) {
  if (anterior == null) return null;
  if (!anterior) {
    return <span className="text-xs text-slate-500">antes: 0</span>;
  }
  const pct = Math.round(((actual - anterior) / anterior) * 100);
  const Icono = pct > 0 ? ArrowUpRight : pct < 0 ? ArrowDownRight : Minus;
  const color = pct > 0 ? 'text-[#7BC24A]' : pct < 0 ? 'text-red-400' : 'text-slate-400';
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${color}`}>
      <Icono className="h-3.5 w-3.5" />
      {pct > 0 ? '+' : ''}{pct}%
      <span className="text-slate-500 font-normal ml-1">vs {anterior}</span>
    </span>
  );
}

function Kpi({ label, valor, sub, actual, anterior, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      <p className="text-2xl font-bold text-white mt-1.5 tracking-tight">{valor}</p>
      <div className="mt-1 min-h-[18px] flex flex-wrap items-center gap-x-2">
        {sub && <span className="text-xs text-slate-500">{sub}</span>}
        <Variacion actual={actual} anterior={anterior} />
      </div>
    </div>
  );
}

export function Bloque({ numero, pregunta, children, testid }) {
  return (
    <section className="mb-10" data-testid={testid}>
      <h2 className="flex items-baseline gap-3 mb-4">
        <span className="text-[#5B9B2F] font-mono text-sm font-bold">{numero}</span>
        <span className="text-xl font-bold text-white tracking-tight">{pregunta}</span>
      </h2>
      {children}
    </section>
  );
}

function Tarjeta({ titulo, detalle, acciones, children, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid={testid}>
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-100">{titulo}</h3>
          {detalle && <p className="text-xs text-slate-500 mt-0.5">{detalle}</p>}
        </div>
        {acciones}
      </div>
      {children}
    </div>
  );
}

function Vacio({ children }) {
  return <p className="text-sm text-slate-500 py-2">{children}</p>;
}

// ---------------------------------------------------------------------------
// 1. ¿Estoy vendiendo?
// ---------------------------------------------------------------------------

function mesActual() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function FormGasto({ cargados, onGuardar }) {
  const [mes, setMes] = useState(mesActual());
  const [monto, setMonto] = useState('');
  const [guardando, setGuardando] = useState(false);

  const guardar = async (e) => {
    e.preventDefault();
    const valor = monto.trim() === '' ? null : Number(monto.replace(/\./g, '').replace(',', '.'));
    if (valor !== null && (!Number.isFinite(valor) || valor < 0)) {
      toast.error('Poné el gasto en pesos, sin signos');
      return;
    }
    setGuardando(true);
    try {
      await onGuardar(mes, valor);
      setMonto('');
      toast.success(valor === null ? `Gasto de ${mes} borrado` : `Gasto de ${mes} guardado`);
    } catch (err) {
      toast.error(err.message || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <form onSubmit={guardar} className="space-y-2" data-testid="form-gasto">
      <div className="flex flex-wrap gap-2">
        <Input
          type="month"
          value={mes}
          onChange={(e) => setMes(e.target.value)}
          className="w-40 h-8 bg-white/5 border-white/10 text-white text-sm"
          data-testid="gasto-mes"
        />
        <Input
          inputMode="numeric"
          value={monto}
          onChange={(e) => setMonto(e.target.value)}
          placeholder="Gasto del mes en $"
          className="w-44 h-8 bg-white/5 border-white/10 text-white text-sm"
          data-testid="gasto-monto"
        />
        <Button
          type="submit"
          size="sm"
          disabled={guardando}
          className="h-8 bg-[#5B9B2F] hover:bg-[#4a8126] text-white"
          data-testid="gasto-guardar"
        >
          {guardando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Guardar'}
        </Button>
      </div>
      <p className="text-[11px] text-slate-500">
        Lo gastado en Meta Ads ese mes. Vacío borra el mes.
        {cargados.length > 0 && (
          <> Cargados: {cargados.map((g) => `${g.mes} ${fmtMoney(g.monto)}`).join(' · ')}</>
        )}
      </p>
    </form>
  );
}

export function BloqueVendiendo({ datos, onGuardarGasto }) {
  const v = datos;
  const unidad = v.serie_unidad === 'semana' ? 'por semana' : 'por día';
  return (
    <Bloque numero="1" pregunta="¿Estoy vendiendo?" testid="bloque-vendiendo">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <Kpi
          label="Ventas"
          valor={v.ventas.cantidad}
          sub={fmtMoney(v.ventas.monto)}
          actual={v.ventas.cantidad}
          anterior={v.ventas.cantidad_anterior}
          testid="kpi-ventas"
        />
        <Kpi
          label="Del checkout a la venta"
          valor={v.conversion_pct == null ? '—' : `${v.conversion_pct}%`}
          sub={`${v.checkouts} ${v.checkouts === 1 ? 'checkout iniciado' : 'checkouts iniciados'}`}
          testid="kpi-conversion"
        />
        <Kpi
          label="Costo por venta"
          valor={v.costo_por_venta == null ? '—' : fmtMoney(v.costo_por_venta)}
          sub={v.gasto_anuncios == null
            ? 'Cargá el gasto en anuncios'
            : `${fmtMoney(v.gasto_anuncios)} en anuncios`}
          testid="kpi-costo"
        />
        <Kpi
          label="Leads"
          valor={v.leads}
          sub={`Demo: ${v.demo.iniciadas} iniciadas, ${v.demo.completadas} completas`}
          actual={v.leads}
          anterior={v.leads_anterior}
          testid="kpi-leads"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Tarjeta titulo={`Ventas ${unidad}`} testid="grafico-ventas">
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={v.serie} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="etiqueta" tick={{ fill: '#94a3b8', fontSize: 10 }} tickLine={false}
                       axisLine={false} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fill: '#94a3b8', fontSize: 10 }} tickLine={false}
                       axisLine={false} />
                <Tooltip
                  cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                  contentStyle={{ background: '#0D1B2A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}
                  labelStyle={{ color: '#e2e8f0' }}
                  formatter={(valor, _nombre, item) => [`${valor} · ${fmtMoney(item.payload.monto)}`, 'Ventas']}
                />
                <Bar dataKey="ventas" fill="#5B9B2F" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Tarjeta>

        <Tarjeta titulo="Por producto" testid="ventas-por-producto">
          {v.por_producto.length === 0 ? (
            <Vacio>Sin ventas en el período.</Vacio>
          ) : (
            <ul className="space-y-2">
              {v.por_producto.map((p) => (
                <li key={p.producto} className="flex items-center justify-between text-sm">
                  <span className="text-slate-200">{p.nombre}</span>
                  <span className="text-slate-400">
                    <span className="text-white font-semibold">{p.cantidad}</span> · {fmtMoney(p.monto)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Tarjeta>

        <Tarjeta
          titulo="Gasto en anuncios"
          detalle="El panel lo reparte por día para calcular el costo por venta del período."
          testid="tarjeta-gasto"
        >
          <FormGasto cargados={v.gastos_cargados} onGuardar={onGuardarGasto} />
        </Tarjeta>
      </div>
    </Bloque>
  );
}

// ---------------------------------------------------------------------------
// 2. ¿Lo están usando?
// ---------------------------------------------------------------------------

function TablaClientes({ filas, columnas, testid }) {
  return (
    <div className="overflow-x-auto max-h-72 overflow-y-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="text-slate-500 text-[11px] uppercase tracking-wider">
          <tr>
            <th className="py-1.5 pr-3 text-left font-medium">Mail</th>
            {columnas.map((c) => (
              <th key={c.titulo} className={`py-1.5 pr-3 font-medium ${c.derecha ? 'text-right' : 'text-left'}`}>
                {c.titulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {filas.map((f) => (
            <tr key={f.email}>
              <td className="py-1.5 pr-3 text-slate-200 break-all">
                <a href={`mailto:${f.email}`} className="hover:text-[#7BC24A] hover:underline">{f.email}</a>
              </td>
              {columnas.map((c) => (
                <td key={c.titulo} className={`py-1.5 pr-3 text-slate-400 whitespace-nowrap ${c.derecha ? 'text-right' : ''}`}>
                  {c.valor(f)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BloqueUso({ datos }) {
  const u = datos;
  return (
    <Bloque numero="2" pregunta="¿Lo están usando?" testid="bloque-uso">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
        <Kpi
          label="Usaron EVALPRO"
          valor={`${u.activos} de ${u.clientes}`}
          sub="clientes con al menos una entrevista"
          actual={u.activos}
          anterior={u.activos_anterior}
          testid="kpi-activos"
        />
        <Kpi
          label="Entrevistas hechas"
          valor={u.entrevistas}
          actual={u.entrevistas}
          anterior={u.entrevistas_anterior}
          testid="kpi-entrevistas"
        />
        <Kpi
          label="Nunca arrancaron"
          valor={u.nunca_entrevistaron.length}
          sub="tienen acceso y ninguna entrevista"
          testid="kpi-nunca"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Tarjeta titulo="Los que más la usan" detalle="Primero, los que más entrevistaron en el período." testid="ranking">
          {u.ranking.length === 0 ? <Vacio>Todavía nadie hizo una entrevista.</Vacio> : (
            <TablaClientes
              filas={u.ranking}
              columnas={[
                { titulo: 'Período', derecha: true, valor: (f) => f.periodo },
                { titulo: 'Total', derecha: true, valor: (f) => f.total },
                { titulo: 'Última', valor: (f) => fmtFecha(f.ultima) },
              ]}
            />
          )}
        </Tarjeta>

        <Tarjeta
          titulo="Tienen acceso y nunca hicieron una entrevista"
          detalle="Un mensaje a tiempo evita un reembolso."
          acciones={<BotonesMails emails={u.nunca_entrevistaron.map((f) => f.email)} testid="copiar-nunca" />}
          testid="nunca-entrevistaron"
        >
          {u.nunca_entrevistaron.length === 0 ? <Vacio>Todos arrancaron.</Vacio> : (
            <TablaClientes
              filas={u.nunca_entrevistaron}
              columnas={[{ titulo: 'Alta', valor: (f) => `${fmtFecha(f.alta)} (${f.dias_desde_alta} d)` }]}
            />
          )}
        </Tarjeta>

        <Tarjeta
          titulo={`Dejaron de usar (más de ${u.dias_sin_uso} días)`}
          detalle="Los que más habían usado, primero."
          acciones={<BotonesMails emails={u.dejaron_de_usar.map((f) => f.email)} testid="copiar-dejaron" />}
          testid="dejaron-de-usar"
        >
          {u.dejaron_de_usar.length === 0 ? <Vacio>Nadie dejó de usar.</Vacio> : (
            <TablaClientes
              filas={u.dejaron_de_usar}
              columnas={[
                { titulo: 'Total', derecha: true, valor: (f) => f.total },
                { titulo: 'Última', valor: (f) => `${fmtFecha(f.ultima)} (${f.dias_sin_usar} d)` },
              ]}
            />
          )}
        </Tarjeta>
      </div>
    </Bloque>
  );
}

// ---------------------------------------------------------------------------
// 3. ¿Qué tengo que hacer?
// ---------------------------------------------------------------------------

function TablaPagos({ filas, testid }) {
  return (
    <div className="overflow-x-auto max-h-72 overflow-y-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="text-slate-500 text-[11px] uppercase tracking-wider">
          <tr>
            <th className="py-1.5 pr-3 text-left font-medium">Mail</th>
            <th className="py-1.5 pr-3 text-left font-medium">Cuándo</th>
            <th className="py-1.5 pr-3 text-left font-medium">Qué</th>
            <th className="py-1.5 pr-3 text-left font-medium">Motivo</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {filas.map((f) => (
            <tr key={f.email} className="align-top">
              <td className="py-2 pr-3 text-slate-200 break-all">
                <a href={`mailto:${f.email}`} className="hover:text-[#7BC24A] hover:underline">{f.email}</a>
                {f.intentos > 1 && <span className="block text-[11px] text-slate-500">{f.intentos} intentos</span>}
              </td>
              <td className="py-2 pr-3 text-slate-400 whitespace-nowrap">{fmtFecha(f.fecha, true)}</td>
              <td className="py-2 pr-3 text-slate-400 whitespace-nowrap">
                {f.producto} · {fmtMoney(f.monto)}
                {f.metodo && <span className="block text-[11px] text-slate-500">{f.metodo}</span>}
              </td>
              <td className="py-2 pr-3 text-slate-300">
                {f.motivo.texto}
                {f.motivo.recuperable && (
                  <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#5B9B2F]/15 text-[#7BC24A] border border-[#5B9B2F]/30">
                    Vale escribirle
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ESTADO_CAPACITACION = {
  pendiente: { texto: 'Sin invitar', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  invitado: { texto: 'Invitado', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
};

function Capacitacion({ datos, onMarcar }) {
  const [ocupado, setOcupado] = useState(null);
  const sinInvitar = datos.sin_capacitar.filter((c) => c.estado === 'pendiente').map((c) => c.email);

  const marcar = async (emails, estado, clave) => {
    if (estado === 'quitado' && !window.confirm(`¿Sacar a ${emails[0]} de la lista de capacitación?`)) return;
    setOcupado(clave);
    try {
      await onMarcar(emails, estado);
    } catch (e) {
      toast.error(e.message || 'No se pudo guardar');
    } finally {
      setOcupado(null);
    }
  };

  return (
    <Tarjeta
      titulo="Capacitación por Meet"
      detalle={`${datos.compradores} compradores · ${datos.asistieron} fueron · ${datos.invitados} invitados · ${datos.pendientes} sin invitar${datos.quitados ? ` · ${datos.quitados} quitados` : ''}`}
      acciones={sinInvitar.length > 0 && (
        <div className="flex items-center gap-2">
          <BotonesMails emails={sinInvitar} testid="copiar-sin-invitar" />
          <Button
            size="sm"
            onClick={() => marcar(sinInvitar, 'invitado', 'todos')}
            disabled={ocupado !== null}
            className="h-7 bg-[#5B9B2F] hover:bg-[#4a8126] text-white text-xs"
            data-testid="marcar-invitados"
          >
            {ocupado === 'todos' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3 mr-1.5" />}
            Marcar {sinInvitar.length} como invitados
          </Button>
        </div>
      )}
      testid="capacitacion"
    >
      {datos.sin_capacitar.length === 0 ? (
        <Vacio>{datos.compradores ? 'Todos los compradores ya hicieron la capacitación.' : 'Todavía no hay compradores.'}</Vacio>
      ) : (
        <div className="overflow-x-auto max-h-72 overflow-y-auto">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-white/5">
              {datos.sin_capacitar.map((c) => {
                const e = ESTADO_CAPACITACION[c.estado];
                return (
                  <tr key={c.email}>
                    <td className="py-1.5 pr-3 text-slate-200 break-all">{c.email}</td>
                    <td className="py-1.5 pr-3 text-slate-500 text-xs whitespace-nowrap">compró {fmtFecha(c.compra)}</td>
                    <td className="py-1.5 pr-3">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold border ${e.cls}`}>
                        {e.texto}{c.invitado ? ` ${fmtFecha(c.invitado)}` : ''}
                      </span>
                    </td>
                    <td className="py-1.5 text-right whitespace-nowrap">
                      {c.estado === 'pendiente' && (
                        <button
                          type="button"
                          onClick={() => marcar([c.email], 'invitado', c.email)}
                          disabled={ocupado !== null}
                          className="text-xs text-blue-300 hover:underline mr-3"
                        >
                          Invitado
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => marcar([c.email], 'asistio', c.email)}
                        disabled={ocupado !== null}
                        className="text-xs text-[#7BC24A] hover:underline inline-flex items-center gap-1"
                        data-testid={`asistio-${c.email}`}
                      >
                        <UserCheck className="h-3 w-3" /> Asistió
                      </button>
                      {c.estado === 'invitado' && (
                        <button
                          type="button"
                          onClick={() => marcar([c.email], 'pendiente', c.email)}
                          disabled={ocupado !== null}
                          className="text-xs text-slate-500 hover:underline ml-3 inline-flex items-center gap-1"
                          title="Deshacer"
                        >
                          <Undo2 className="h-3 w-3" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => marcar([c.email], 'quitado', c.email)}
                        disabled={ocupado !== null}
                        className="text-xs text-slate-500 hover:text-red-300 ml-3 inline-flex items-center"
                        title="Sacar de la lista"
                        data-testid={`quitar-${c.email}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Tarjeta>
  );
}

export function BloqueTareas({ datos, onMarcarCapacitacion }) {
  const t = datos;
  const [verLeads, setVerLeads] = useState(false);
  const leads = verLeads ? t.leads_sin_compra : t.leads_sin_compra.slice(0, 8);
  const nada = !t.rechazados.length && !t.pendientes.length;
  return (
    <Bloque numero="3" pregunta="¿Qué tengo que hacer?" testid="bloque-tareas">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Tarjeta
          titulo={`Pagos rechazados (${t.rechazados.length})`}
          detalle="La venta más fácil de recuperar: esa persona quería comprar."
          acciones={<BotonesMails emails={t.rechazados.map((f) => f.email)} testid="copiar-rechazados" />}
          testid="rechazados"
        >
          {t.rechazados.length === 0 ? <Vacio>Ninguno sin resolver en el período.</Vacio> : (
            <TablaPagos filas={t.rechazados} testid="tabla-rechazados" />
          )}
        </Tarjeta>

        <Tarjeta
          titulo={`Pagos pendientes (${t.pendientes.length})`}
          detalle="Efectivo o transferencia que todavía no se acreditó."
          acciones={<BotonesMails emails={t.pendientes.map((f) => f.email)} testid="copiar-pendientes" />}
          testid="pendientes"
        >
          {t.pendientes.length === 0 ? <Vacio>Nada pendiente en el período.</Vacio> : (
            <TablaPagos filas={t.pendientes} testid="tabla-pendientes" />
          )}
        </Tarjeta>

        <Capacitacion datos={t.capacitacion} onMarcar={onMarcarCapacitacion} />

        <Tarjeta
          titulo={`Leads que no compraron (${t.leads_sin_compra.length})`}
          detalle="Bajaron la guía en el período y todavía no compraron."
          acciones={<BotonesMails emails={t.leads_sin_compra.map((f) => f.email)} testid="copiar-leads" />}
          testid="leads-sin-compra"
        >
          {t.leads_sin_compra.length === 0 ? <Vacio>Ninguno en el período.</Vacio> : (
            <>
              <ul className="text-sm divide-y divide-white/5">
                {leads.map((l) => (
                  <li key={l.email} className="py-1.5 flex justify-between gap-3">
                    <span className="text-slate-200 break-all">{l.email}</span>
                    <span className="text-slate-500 text-xs whitespace-nowrap">{fmtFecha(l.fecha)}</span>
                  </li>
                ))}
              </ul>
              {t.leads_sin_compra.length > 8 && (
                <button
                  type="button"
                  onClick={() => setVerLeads(!verLeads)}
                  className="text-xs text-slate-400 hover:text-white mt-2"
                >
                  {verLeads ? 'Ver menos' : `Ver los ${t.leads_sin_compra.length}`}
                </button>
              )}
            </>
          )}
        </Tarjeta>
      </div>
      {nada && (
        <p className="text-xs text-slate-500 mt-3 inline-flex items-center gap-1.5">
          <CheckCircle2 className="h-3.5 w-3.5 text-[#5B9B2F]" />
          Ningún pago trabado en el período.
        </p>
      )}
    </Bloque>
  );
}

// ---------------------------------------------------------------------------
// 4. Herramientas
// ---------------------------------------------------------------------------

export function AutorizarEnLote({ onAutorizar }) {
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);

  const enviar = async () => {
    if (!texto.trim()) return;
    setEnviando(true);
    try {
      const r = await onAutorizar(texto);
      setResultado(r);
      if (r.autorizados.length) setTexto('');
      toast.success(`${r.autorizados.length} ${r.autorizados.length === 1 ? 'mail autorizado' : 'mails autorizados'}`);
    } catch (e) {
      toast.error(e.message || 'No se pudo autorizar');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Tarjeta
      titulo="Autorizar mails en lote"
      detalle="Acceso ilimitado para cuentas de regalo, un curso o una institución. Separados por coma, espacio o renglón."
      testid="autorizar-lote"
    >
      <textarea
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        rows={4}
        placeholder={'profe1@mail.com\nprofe2@mail.com'}
        className="w-full rounded-lg bg-white/5 border border-white/10 text-white text-sm p-2.5 placeholder:text-slate-600"
        data-testid="autorizar-lote-texto"
      />
      <div className="flex items-center justify-between gap-3 mt-2">
        <p className="text-[11px] text-slate-500">
          Un mail que ya tenía acceso queda como estaba: no se pisa un pack ni un pago.
        </p>
        <Button
          size="sm"
          onClick={enviar}
          disabled={enviando || !texto.trim()}
          className="bg-[#5B9B2F] hover:bg-[#4a8126] text-white shrink-0"
          data-testid="autorizar-lote-btn"
        >
          {enviando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Autorizar'}
        </Button>
      </div>
      {resultado && (
        <div className="mt-3 space-y-1 text-xs" data-testid="autorizar-lote-resultado">
          {resultado.autorizados.length > 0 && (
            <p className="text-[#7BC24A]">Autorizados: {resultado.autorizados.join(', ')}</p>
          )}
          {resultado.ya_tenian.length > 0 && (
            <p className="text-slate-400">
              Ya tenían acceso: {resultado.ya_tenian.map((y) => `${y.email} (${y.tipo === 'creditos' ? 'pack' : y.tipo})`).join(', ')}
            </p>
          )}
          {resultado.invalidos.length > 0 && (
            <p className="text-red-300">No parecen mails: {resultado.invalidos.join(', ')}</p>
          )}
        </div>
      )}
    </Tarjeta>
  );
}
